import { faqsPl } from "@data/firmware-embedded-landing/faq";
import {
  aboutUsCopyPl,
  contactFormPl,
  faqStructuredData,
  iotClusterLinks,
  iotKnowledgeBaseLinks,
  organizationStructuredData,
  serviceStructuredData,
} from "@data/iot-landing/shared";
import {
  Calendar,
  CheckCircle,
  Cloud,
  Code,
  Cpu,
  FileText,
  FlaskConical,
  Layers,
  LifeBuoy,
  PenTool,
  Radio,
  RefreshCw,
  Rocket,
  Search,
  Shield,
  Smartphone,
} from "lucide-react";

import type { FAQCopy } from "@components/bluetooth-landing/FAQ";
import type { FooterCopy } from "@components/bluetooth-landing/Footer";
import type { HeroCopy } from "@components/bluetooth-landing/Hero";
import type { ProcessCopy } from "@components/bluetooth-landing/Process";
import type { ServicesCopy } from "@components/bluetooth-landing/Services";
import type { TargetAudienceCopy } from "@components/bluetooth-landing/TargetAudience";
import type { ValuePropositionCopy } from "@components/bluetooth-landing/ValueProposition";

export const title =
  "Programowanie firmware embedded - od PoC po produkcję";

export const description =
  "Programowanie firmware embedded od PoC po produkcję — STM32, ESP32, Nordic nRF, Zephyr RTOS, firmware LTE-M i NB-IoT oraz bezpieczne aktualizacje OTA/DFU. Polski zespół od jednego urządzenia po flotę.";

export const structuredData = [
  organizationStructuredData(description),
  serviceStructuredData(
    "Programowanie firmware embedded",
    description,
    "Embedded firmware development",
  ),
  faqStructuredData(faqsPl),
];

export const heroCopy: HeroCopy = {
  header: "Programowanie firmware",
  headerTail: "embedded",
  description:
    "Piszemy firmware embedded na STM32, ESP32, Nordic nRF i Zephyr RTOS — od szybkiego PoC na DevKit po produkcyjny kod z OTA, optymalizacją energii i integracją z chmurą AWS IoT. Firmware LTE-M i NB-IoT to u nas standard, nie dodatek.",
  cta: {
    text: "Darmowa konsultacja",
    href: "#kontakt",
  },
  secondaryCta: {
    text: "Poznaj nasz proces",
    href: "#proces",
  },
  benefits: [
    { text: "STM32, ESP32, Nordic nRF", Icon: CheckCircle },
    { text: "OTA/DFU i LTE-M / NB-IoT", Icon: CheckCircle },
    { text: "PoC w tygodnie, produkcja w miesiące", Icon: CheckCircle },
  ],
};

export const targetAudienceCopy: TargetAudienceCopy = {
  header: {
    h2: "Kim są nasi klienci?",
    h3: "Dla kogo jest programowanie firmware embedded?",
    p: "Klienci rzadko proszą o sam firmware — częściej o urządzenie z aplikacją, chmurą i panelem. My piszemy warstwę embedded, ale rozumiemy cały stack. Czy któryś z tych scenariuszy pasuje do Ciebie?",
  },
  points: [
    {
      Icon: Cpu,
      title: "Masz hardware i potrzebujesz firmware od zera",
      description:
        "PCB jest gotowe albo leży DevKit na biurku — potrzebujesz firmware, który odczytuje sensory, komunikuje się z chmurą i pracuje na baterii tak długo, jak obiecałeś klientowi.",
    },
    {
      Icon: Radio,
      title: "Chcesz PoC łączności LTE-M lub NB-IoT",
      description:
        "Masz działający produkt IoT i chcesz dodać łączność LPWA — integrujemy moduły LTE-M/NB-IoT, zarządzamy połączeniem i oszczędzaniem energii w firmware.",
    },
    {
      Icon: RefreshCw,
      title: "Potrzebujesz OTA/DFU w produkcie Nordic lub STM32",
      description:
        "Urządzenie ma żyć lata i wymaga bezpiecznych aktualizacji firmware — projektujemy pipeline OTA/DFU razem z aplikacją lub backendem, nie jako osobny projekt.",
    },
    {
      Icon: Cloud,
      title: "Integrujesz sensory ESP32 z AWS IoT",
      description:
        "Czujniki temperatury, wilgotności czy jakości powietrza mają trafiać do chmury — piszemy firmware z MQTT/HTTP, certyfikatami i logiką edge pod AWS IoT Core.",
    },
    {
      Icon: Layers,
      title: "Przejmujesz kod firmware od poprzedniego dostawcy",
      description:
        "Dostałeś repozytorium w spadku i nie wiesz, co jest ryzykiem — zaczynamy od audytu, mapujemy problemy i proponujemy plan dokończenia albo refaktoryzacji.",
    },
  ],
  cta: {
    h4: "Brzmi znajomo?",
    text: "Opowiedz nam o swoim urządzeniu i platformie — nawet jeśli nie jesteś pewien, czy wybrać Zephyr czy bare-metal. Doradzimy i zaproponujemy zakres.",
    href: "#kontakt",
    buttonText: "Umów darmową konsultację",
  },
};

export const valuePropositionCopy: ValuePropositionCopy = {
  header: {
    h2: "Nasza specjalizacja",
    h3: "Dlaczego warto pisać firmware embedded z nami?",
    p: "Programowanie firmware to nie tylko kod na mikrokontrolerze — to integracja z hardware, aplikacją mobilną, chmurą i procesem produkcyjnym. Budujemy firmware embedded, który działa w realnym produkcie, nie tylko na płytce deweloperskiej.",
    imageAlt: "Programowanie firmware embedded — STM32, ESP32, Nordic nRF",
  },
  values: [
    {
      Icon: Cpu,
      title: "STM32, ESP32, Nordic nRF — dobór pod produkt",
      description:
        "Nie jesteśmy wierni jednej platformie — wybieramy mikrokontroler pod baterię, łączność, koszt i wymagania produkcyjne. Zephyr RTOS stosujemy tam, gdzie ma sens długi cykl życia produktu.",
    },
    {
      Icon: RefreshCw,
      title: "Bezpieczne aktualizacje OTA i DFU",
      description:
        "Projektujemy pipeline aktualizacji firmware — podpis cyfrowy, rollback, aktualizacja przez BLE, Wi-Fi lub LTE-M. Nordic DFU i własne mechanizmy OTA to codzienność, nie eksperyment.",
    },
    {
      Icon: Radio,
      title: "Firmware LTE-M i NB-IoT od PoC po produkcję",
      description:
        "Integrujemy moduły LPWA, zarządzamy reconnectem, oszczędzaniem energii i wysyłką danych telemetrycznych — firmware, które realnie działa poza zasięgiem Wi-Fi.",
    },
    {
      Icon: Shield,
      title: "Optymalizacja energii i stabilności",
      description:
        "Wiemy, jak debugować problemy z baterią, resetami i utratą połączenia — typowe bolączki produktów IoT, które wychodzą dopiero w terenie, nie na biurku.",
    },
    {
      Icon: Smartphone,
      title: "Firmware + aplikacja + backend w jednym zespole",
      description:
        "W leadach klienci proszą o firmware i aplikację mobilną w jednym zapytaniu — dlatego integrujemy warstwy w jednym projekcie, zamiast przerzucać problemy między dostawcami.",
    },
  ],
};

export const servicesCopy: ServicesCopy = {
  header: {
    h2: "Usługi",
    h3: "Programowanie firmware embedded",
    p: "Od szybkiego PoC na DevKit po produkcyjny firmware z OTA, testami i dokumentacją. Piszemy kod, który działa na flocie urządzeń, nie tylko na jednym egzemplarzu.",
  },
  services: [
    {
      Icon: Search,
      title: "Analiza wymagań i dobór platformy",
      description:
        "Analizujemy hardware, wymagania bateryjne i łączności, dobieramy STM32, ESP32, Nordic nRF lub Zephyr RTOS — z uzasadnieniem technicznym i kosztowym.",
    },
    {
      Icon: Code,
      title: "Firmware PoC na DevKit",
      description:
        "Szybki proof of concept — odczyt sensorów, komunikacja BLE/Wi-Fi/LTE-M, podstawowa logika i diagnostyka. Działający firmware w rękach w kilka tygodni.",
    },
    {
      Icon: Radio,
      title: "Firmware LTE-M i NB-IoT",
      description:
        "Integracja modułów LPWA, zarządzanie sesją, oszczędzanie energii, wysyłka danych telemetrycznych i obsługa roamingu w firmware embedded.",
    },
    {
      Icon: RefreshCw,
      title: "Aktualizacje OTA / DFU",
      description:
        "Projektujemy i wdrażamy bezpieczny pipeline aktualizacji firmware — Nordic DFU, własne OTA przez Wi-Fi/LTE, podpis i weryfikacja integralności.",
    },
    {
      Icon: Cloud,
      title: "Integracja z chmurą i protokołami IoT",
      description:
        "MQTT, HTTP, AWS IoT Core, własne API — firmware wysyła dane do chmury, odbiera konfigurację i obsługuje edge logic tam, gdzie to ma sens.",
    },
    {
      Icon: FlaskConical,
      title: "Testy firmware i HIL",
      description:
        "Testy integracyjne, hardware-in-the-loop i testy regresji — kolejne wersje firmware nie psują tego, co już działa w terenie.",
    },
    {
      Icon: Layers,
      title: "Refaktoryzacja i przejęcie kodu",
      description:
        "Przejmujemy firmware od poprzedniego dostawcy — audyt, mapa ryzyk, plan refaktoryzacji i dokończenie funkcji bez przepisywania wszystkiego od zera.",
    },
    {
      Icon: LifeBuoy,
      title: "Utrzymanie i kolejne wersje",
      description:
        "Wsparcie po premierze — nowe funkcje, poprawki stabilności, aktualizacje SDK i adaptacja pod kolejne rewizje hardware.",
    },
  ],
  cta: {
    h4: "Nie wiesz, od czego zacząć?",
    p: "Opisz nam urządzenie, platformę i cele — nawet jeśli masz tylko DevKit i pomysł. Podpowiemy, jak zaplanować firmware PoC.",
    button: {
      text: "Umów darmową konsultację",
      href: "#kontakt",
    },
  },
};

export const processCopy: ProcessCopy = {
  header: {
    h2: "Nasz Proces",
    h3: "Jak powstaje firmware embedded",
    p: "Przejrzyste etapy od analizy po produkcyjny kod z OTA. Zobacz, jak przechodzimy od DevKit do firmware gotowego na flotę urządzeń.",
  },
  stepNumber: "Krok",
  steps: [
    {
      Icon: Calendar,
      title: "Darmowe spotkanie online",
      duration: "w kilka godzin",
      description:
        "30 minut rozmowy z inżynierem firmware. Opisujesz urządzenie, platformę i wymagania — dostajesz pierwszą ocenę wykonalności i orientacyjną wycenę.",
    },
    {
      Icon: FileText,
      title: "Analiza hardware i architektury",
      duration: "w kilka dni",
      description:
        "Mapujemy peryferia, łączność, wymagania bateryjne i integrację z aplikacją/chmurą. Otrzymujesz plan architektury firmware i harmonogram.",
    },
    {
      Icon: PenTool,
      title: "Podpisanie umowy i NDA",
      duration: "ustalenie harmonogramu",
      description:
        "Ustalamy zakres PoC lub produkcji, kamienie milowe i sposób dostarczania wersji firmware do testów.",
    },
    {
      Icon: Code,
      title: "Firmware PoC",
      duration: "2–6 tygodni",
      description:
        "Budujemy działający firmware na DevKit — sensory, komunikacja, logika urządzenia i podstawowa diagnostyka gotowa do testów.",
    },
    {
      Icon: FlaskConical,
      title: "Testy w terenie i iteracje",
      duration: "2–4 tygodnie",
      description:
        "Testujemy firmware w realnych warunkach — bateria, łączność, stabilność. Wprowadzamy poprawki na podstawie wyników, nie domysłów.",
    },
    {
      Icon: RefreshCw,
      title: "OTA, optymalizacja i hardening",
      duration: "min. 2–4 tygodnie",
      description:
        "Wdrażamy aktualizacje OTA/DFU, optymalizujemy pobór prądu i uszczelniamy kod pod wymagania produkcyjne.",
    },
    {
      Icon: Rocket,
      title: "Firmware produkcyjny i dokumentacja",
      duration: "min. 2 tygodnie",
      description:
        "Przygotowujemy wersję produkcyjną z testami regresji, dokumentacją API i instrukcją flashowania na linii produkcyjnej.",
    },
    {
      Icon: CheckCircle,
      title: "Przekazanie i utrzymanie",
      duration: "na stałe",
      description:
        "Otrzymujesz repozytorium, dokumentację i działający firmware — albo kontynuujemy współpracy przy utrzymaniu i kolejnych wersjach.",
    },
  ],
  cta: {
    href: "#kontakt",
    text: "Zamów firmware embedded",
  },
};

export const aboutUsCopy = aboutUsCopyPl({
  imageAlt: "Zespół Blues Brackets — programowanie firmware embedded",
});

export const faqCopy: FAQCopy = {
  header: {
    h2: "FAQ",
    h3: "Najczęściej zadawane pytania o firmware embedded",
  },
  faqs: faqsPl,
};

export const footerCopy: FooterCopy = {
  header: {
    h2: "Potrzebujesz firmware embedded?",
    p: "Umów się na darmową konsultację — opowiedz o urządzeniu, platformie i wymaganiach OTA, a zaproponujemy plan od PoC po produkcję.",
  },
  cta: {
    href: "#kontakt",
    text: "Umów darmową konsultację",
    p: "Programowanie firmware embedded — STM32, ESP32, Nordic nRF, Zephyr, LTE-M/NB-IoT i aktualizacje OTA. Od PoC po produkcyjny kod na flotę urządzeń.",
    email: "hello@bluesbrackets.com",
    phone: "+48 535 462 678",
  },
  links: {
    h4: "Na skróty",
    links: [
      { href: "#uslugi", text: "Nasze usługi" },
      { href: "#proces", text: "Proces współpracy" },
      { href: "#faq", text: "FAQ" },
      { href: "/iot", text: "Systemy IoT" },
      ...iotClusterLinks.filter(
        l => l.href !== "/iot" && l.href !== "/firmware-embedded",
      ),
    ],
  },
  knowledgeBase: {
    h4: "Baza wiedzy",
    links: [...iotKnowledgeBaseLinks],
  },
  copyright: "bluesBrackets. Wszelkie prawa zastrzeżone.",
  privacyPolicy: "Polityka prywatności",
  privacyPolicyHref: "/policy",
  contactForm: contactFormPl,
};
